import React from "react";
import styled from "@emotion/styled";

import NoticeStage from "lib/core_components/NoticeStage";
import { useSubscription } from "lib/hooks/subscription";
import { NoticesStagesv2, OpportunityEntityType } from "lib/types/graphQLEnums";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { useRecordViewer } from "../../../lib/providers/RecordViewer";
import { sysBackgroundAlternative, sysTextSecondary } from "../../../lib/themes/colors";
import { Flex, Text } from "../../../styles/utility-components";
import { getNoticeDateValue } from "../../notices/utils";
import { opportunitySavedCardContainer } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import FindEntity from "./FindEntity";
import PaidTag from "./PaidTag";
import Tag from "./Tag";

const UNKNOWN_STAGE = "unknown";

type Notice = OpportunityDetails["notices"][0];

type Props = {
  notices: OpportunityDetails["notices"];
  entityUrl: string;
  opportunityId: string;
};

export function OpportunityNotices({ notices, entityUrl, opportunityId }: Props) {
  return (
    <Flex column gap={16}>
      {notices.map((notice) => (
        <SavedNoticeCard key={notice.guid} notice={notice} opportunityId={opportunityId} />
      ))}
      <FindEntity entity="notice" url={entityUrl} isEmptyState={!notices.length} />
    </Flex>
  );
}

function SavedNoticeCard({ notice, opportunityId }: { notice: Notice; opportunityId: string }) {
  const { viewRecord } = useRecordViewer();

  return (
    <Container
      onClick={() => viewRecord(notice.guid, { "Context source": "Opportunity details" })}
      aria-label="Notice card"
    >
      <EntityCardContent
        entity="notice"
        entityId={notice.procurementStage.id}
        entityType={OpportunityEntityType.Notice}
        entityName={`${notice.name ?? "Untitled Notice"}`}
        opportunityId={opportunityId}
      >
        <NoticeCard notice={notice} />
      </EntityCardContent>
    </Container>
  );
}

export function NoticeCard({ notice }: { notice: Notice }) {
  const subscription = useSubscription();
  const hasAccess = shouldHaveAwardAccess(notice) ? subscription.hasDataTypes("AWARDS") : true;

  const noticeDateInRelativeFormat = getNoticeDateInRelativeFormat(notice);

  return (
    <Flex column gap={16} width="100%">
      {notice.stagev2 && <NoticeStage stage={notice.stagev2} />}
      <Text>{notice.name || "Untitled Notice"}</Text>
      <Flex gap={8} flexWrap="wrap">
        {noticeDateInRelativeFormat && (
          <Tag
            backgroundColor={sysBackgroundAlternative}
            fontColor={sysTextSecondary}
            title={noticeDateInRelativeFormat}
          />
        )}
        {!hasAccess && <PaidTag />}
      </Flex>
    </Flex>
  );
}

function getNoticeDateInRelativeFormat(notice: Notice) {
  const date = getNoticeDateValue(notice);
  if (!date) return;

  const humanReadableDate = toHumanRelativeDateFromNow(date);
  const stage = notice.stagev2 ?? UNKNOWN_STAGE;
  let actionText;
  switch (stage) {
    case NoticesStagesv2.AwardedContract:
    case NoticesStagesv2.ExpiredContract:
    case NoticesStagesv2.CancelledAward:
      actionText = humanReadableDate.includes("ago") ? "Expired" : "Expiring";
      break;
    case NoticesStagesv2.OpenTender:
    case NoticesStagesv2.ClosedTender:
    case NoticesStagesv2.CancelledTender:
    case NoticesStagesv2.UnknownTender:
      actionText = humanReadableDate.includes("ago") ? "Closed" : "Closing";
      break;
    default:
      actionText = humanReadableDate.includes("ago") ? "Published" : "Publishing";
      break;
  }

  return `${actionText} ${humanReadableDate}`;
}

function shouldHaveAwardAccess(notice: Notice) {
  if (!notice.stagev2) return false;
  return [
    NoticesStagesv2.AwardedContract,
    NoticesStagesv2.ExpiredContract,
    NoticesStagesv2.CancelledAward,
  ].includes(notice.stagev2);
}

const Container = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
}));
